import React, { FC, useEffect, useState } from 'react';
import useTranslations from '@hooks/useTranslations';
import Collapsible from 'react-collapsible';
import parser from 'bbcode-to-react';
import Icon, { Icons } from '@components/Icon';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import { batch, useDispatch, useSelector } from 'react-redux';
import { useMoneyFormatter } from '@hooks/useMoneyFormatter';
import { AccessibleButton } from '@components/Button/AccessibleButton';
import { BasketOptionalFeesDetailsProps } from '@components/v2/BasketOptionalPartsDetails/index';
import UIDuck from '../../../redux/commonDucks/ui.duck';
import { theme } from '../../../styles/theme';
import Spinner from '@components/Spinner';
import Checkbox from '@components/Checkbox';
import { IAccessory } from '../../../services/carDetails/carDetails.types';
import DealService from '../../../services/deal/deal.service';
import { getSessionIdCookie } from '@utils/Session.utils';
import CarDetailsDuck from '../../../redux/carDetails/carDetails.duck';

const MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON = 150;

const BasketOptionalAccessoriesTemplate: FC<BasketOptionalFeesDetailsProps> = ({ className, generatedIndex }) => {
    const { t } = useTranslations();
    const dispatch = useDispatch();
    const { carDetails, currentDeal } = useCarDetailsDuck();
    const { formatMoney } = useMoneyFormatter();
    const isLoading = useSelector((state) => !!UIDuck.getOwnState(state).loading);

    const [isOpen, setIsOpen] = useState(true);
    const [openedLabelIds, setOpenedLabelIds] = useState([]);

    const [accessories, setAccessories] = useState(carDetails?.accessories);

    const handleDescriptionClick = (id: string) => {
        if (openedLabelIds.find((openedId) => openedId === id)) {
            return setOpenedLabelIds(openedLabelIds.filter((openedId) => openedId !== id));
        }

        return setOpenedLabelIds([...openedLabelIds, id]);
    };

    const isOpenedLabelById = (id: string) => openedLabelIds.find((openedId) => id === openedId);

    const handleAccessoryClick = async (accessory: IAccessory) => {
        dispatch(UIDuck.setLoading(5));

        const selectedAccessoriesCodes = accessories
            .filter((selectedAccessory: IAccessory) => selectedAccessory.selected)
            ?.map((selectedAccessory: IAccessory) => selectedAccessory.code);

        const { data } = await DealService.updateDeal(
            {
                accessories: selectedAccessoriesCodes.includes(accessory.code)
                    ? selectedAccessoriesCodes.filter(
                          (selectedAccessoryCode: string) => selectedAccessoryCode !== accessory.code
                      )
                    : [...selectedAccessoriesCodes, accessory.code],
            },
            getSessionIdCookie(),
            true
        );
        setAccessories(data?.deal?.fullProductConfiguration?.accessories || []);

        batch(() => {
            dispatch(CarDetailsDuck.setCurrentDeal(null));
            dispatch(CarDetailsDuck.setCurrentDeal(data?.deal));
            dispatch(CarDetailsDuck.setCurrentCarDetails(data?.deal?.fullProductConfiguration));
            dispatch(UIDuck.setLoading(-5));
        });
    };

    return (
        <div id="optionalAccessories" className={className}>
            <Collapsible
                lazyRender={true}
                open={isOpen}
                trigger={
                    <AccessibleButton onClick={() => setIsOpen(!isOpen)}>
                        <div className="titleWrapper">
                            <div className="titleContainer">
                                <span className="sectionNumber">{generatedIndex}.</span>
                                <span className="title">{`${t(
                                    'basket.optionalAccessories.title'
                                ).toLowerCase()}`}</span>
                            </div>
                            <Icon
                                className={isOpen ? 'isOpen' : 'isClose'}
                                name={isOpen ? Icons.ChevronUp : Icons.ChevronDown}
                            />
                        </div>
                    </AccessibleButton>
                }
            >
                <div className="parts">
                    <p className="parts__description">{t('basket.optionalAccessories.description')}</p>
                    {isLoading ? (
                        <Spinner className="spinnerWrapper" size={20} border={3} color={theme.colors.black} />
                    ) : (
                        accessories.map((accessory: IAccessory, key: number) => (
                            <div key={key} className={`part ${accessory.selected ? 'selected' : ''} accPart`}>
                                <div className="part__name">
                                    <Checkbox
                                        className={'checkbox'}
                                        onChange={() => handleAccessoryClick(accessory)}
                                        checked={accessory.selected}
                                        disabled
                                        name={`${accessory.code}-part-checkbox`}
                                        dataTestId={`TESTING_OPTIONAL_ACCESSORY_${accessory.label?.toUpperCase()}_CHECKBOX`}
                                    >
                                        <span></span>
                                    </Checkbox>
                                    <img src={accessory.image} />
                                    <span>{parser.toReact(accessory.label)}</span>
                                    <div className="part__priceWrapper">
                                        <span className="part__priceWrapper_price" data-testid={`TESTING_OPTIONAL_ACCESSORY_${accessory.label?.toUpperCase()}_PRICE`}>
                                            {accessory.netAmount > 0
                                                ? formatMoney(accessory.netAmount, false)
                                                : t('configurator.colors.includedInPrice')}
                                        </span>
                                    </div>
                                    <div
                                        className={`part__descriptionWrapper ${
                                            accessory.description?.length >
                                            MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON
                                                ? 'part__descriptionWrapper_min'
                                                : ''
                                        } ${isOpenedLabelById(accessory.code) ? 'part__descriptionWrapper_full' : ''}`}
                                    >
                                        <p>{parser.toReact(accessory.description)}</p>
                                    </div>
                                    {accessory.description?.length > MIN_COUNT_OF_CHARACTERS_TO_SHOW_DETAIL_BUTTON && (
                                        <button
                                            onClick={() => handleDescriptionClick(accessory.code)}
                                            className="part__descriptionWrapper_link"
                                        >
                                            {isOpenedLabelById(accessory.code)
                                                ? t('basket.mandatoryFees.detail.hide')
                                                : t('basket.mandatoryFees.detail.show')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </Collapsible>
        </div>
    );
};

export default BasketOptionalAccessoriesTemplate;
